'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState, createContext } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { motion, useAnimation } from 'framer-motion';
import { Howl } from 'howler';
import { BookOpen, Sparkles, Users, Video, Music } from 'lucide-react';

import backgrounds from '@/data/comicBackgrounds';
import portalLottie from '@/lotties/portal.json';

// heavy dynamic imports (ssr false for pageflip & lottie)
const PageFlip = dynamic(() => import('react-pageflip'), { ssr: false });
const Lottie = dynamic(() => import('react-lottie-player'), { ssr: false });
const FeatureCard = dynamic(() => import('@/components/FeatureCard'), { ssr: false });

/* ----------------------------- Theme constants ---------------------------- */
const GOLD_HEX = '#FFD700';
const GOLD_RGBA = '255,215,0';
const BACKGROUND_BLACK = '#030305';
const ACCENT_WHITE = '#FFFFFF';

interface StoryContextType {
  currentPage: number;
  setPage: (n: number) => void;
}
export const StoryContext = createContext<StoryContextType>({
  currentPage: 0,
  setPage: () => {},
});

type SoundMap = {
  page?: Howl;
  whoosh?: Howl;
  portal?: Howl;
};

type Particle = {
  id: string;
  x: number;
  y: number;
  size: number;
  speed: number;
  hue: string;
  rotation: number;
  opacity: number;
  sway: number;
  layer?: 'back' | 'mid' | 'front';
};

/* ===========================================================
   Helper hooks & utilities — ensures reliable clicks on mobile
   =========================================================== */

function useReliableAction<T extends (...args: any[]) => void>(handler: T, options?: { lockMs?: number }) {
  const lockMs = options?.lockMs ?? 420;
  const runningRef = useRef(false);

  const run = useCallback(
    (...args: any[]) => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        handler(...(args as any));
      } catch {
        // ignore
      } finally {
        window.setTimeout(() => {
          runningRef.current = false;
        }, lockMs);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handler, lockMs]
  );

  const props = useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => {
        if ((e as any).button && (e as any).button !== 0) return;
        e.stopPropagation?.();
        run(e);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          run(e);
        }
      },
      role: 'button',
      tabIndex: 0,
      'data-reliable': '1',
      'aria-pressed': false,
    }),
    [run]
  );

  return { run, props };
}

/* inject small runtime CSS to fix mobile tap/overflow quirks */
function injectRuntimeStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('storyclub-runtime-style')) return;

  const css = `
    .btn, button, [data-reliable="1"] {
      -webkit-tap-highlight-color: rgba(0,0,0,0);
      touch-action: manipulation;
      user-select: none;
      -webkit-user-select: none;
      outline: none;
      z-index: 50;
    }
    [data-portal-overlay] { pointer-events: none; }
    [data-portal-overlay].active { pointer-events: auto; z-index: 9999; }
    .icon-hit { padding: 8px; border-radius: 10px; display: inline-grid; place-items: center; }
    .no-horiz-overflow { max-width: 100%; overflow-wrap: break-word; word-break: break-word; min-width: 0; }
    html, body, #__next { overflow-x: hidden; }
  `;
  const s = document.createElement('style');
  s.id = 'storyclub-runtime-style';
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
}

function safePlaySound(s: Howl | undefined) {
  try {
    s?.play();
  } catch {
    // ignore
  }
}

export default function HomePage() {
  const router = useRouter();

  // refs & animation controls
  const pageFlip = useRef<any>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pageControls = useAnimation();
  const overlayControls = useAnimation();
  const portalControls = useAnimation();

  // state
  const [currentPage, setPage] = useState(0);
  const [SFX, setSFX] = useState<SoundMap>({});
  const [portalActive, setPortalActive] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [reduceVisuals, setReduceVisuals] = useState(false);
  const [visibleLottie, setVisibleLottie] = useState(false);
  const [flipSize, setFlipSize] = useState({ width: 900, height: 640 });

  const palette = useMemo(() => [GOLD_HEX, '#FFECB3', '#FFD88A', '#FFEFA2', '#FFFFFF'], []);

  // runtime style fixes
  useEffect(() => {
    injectRuntimeStyle();
  }, []);

  // responsive: ensure flipSize fills mobile width with small padding
  useEffect(() => {
    const update = () => {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      const mobile = w <= 920 || (navigator && /Mobi|Android/i.test(navigator.userAgent));
      setIsMobile(mobile);

      // container padding considered: we aim for full-width flip on small screens
      const horizontalPadding = mobile ? 24 : 40; // match .container padding
      const computedW = Math.max(320, Math.min(w - horizontalPadding, 980));
      const computedH = Math.max(380, Math.round(computedW * (640 / 900))); // preserve aspect ratio
      setFlipSize({ width: computedW, height: computedH });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // particles
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const count = isMobile ? 14 : 56;
    const base: Particle[] = Array.from({ length: count }).map((_, i) => {
      const size = isMobile ? Math.random() * 12 + 6 : Math.random() * 26 + 8;
      const layer = i % 5 === 0 ? 'front' : i % 3 === 0 ? 'mid' : 'back';
      return {
        id: `p-${i}-${Date.now()}`,
        x: Math.random() * w,
        y: Math.random() * h,
        size,
        speed: Math.random() * (isMobile ? 0.48 : 1.1) + (isMobile ? 0.04 : 0.08),
        hue: palette[Math.floor(Math.random() * palette.length)],
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.6 + 0.16,
        sway: (Math.random() - 0.5) * (isMobile ? 120 : 300),
        layer,
      };
    });

    const bubbles: Particle[] = Array.from({ length: Math.max(2, isMobile ? 2 : 6) }).map((_, i) => ({
      id: `bubble-${i}-${Date.now()}`,
      x: Math.random() * w,
      y: Math.random() * (h * 0.6),
      size: isMobile ? Math.random() * 60 + 36 : Math.random() * 140 + 52,
      speed: Math.random() * 0.12 + 0.02,
      hue: GOLD_HEX,
      rotation: Math.random() * 360,
      opacity: Math.random() * 0.38 + 0.22,
      sway: (Math.random() - 0.5) * (isMobile ? 60 : 160),
      layer: 'back',
    }));

    setParticles([...base, ...bubbles]);
  }, [isMobile, palette]);

  // load sounds
  useEffect(() => {
    const soundFiles = [
      { key: 'page' as const, url: '/sounds/page-flip.mp3', volume: 0.45 },
      { key: 'whoosh' as const, url: '/sounds/whoosh.mp3', volume: 0.36 },
      { key: 'portal' as const, url: '/sounds/portal.mp3', volume: 0.7 },
    ];

    const load = async () => {
      if (typeof window === 'undefined') return;
      const map: SoundMap = {};
      await Promise.all(
        soundFiles.map(async ({ key, url, volume }) => {
          try {
            const res = await fetch(url, { method: 'HEAD' });
            if (res.ok) {
              map[key] = new Howl({ src: [url], volume });
            }
          } catch {
            // ignore
          }
        })
      );
      setSFX(map);
    };
    load();
  }, []);

  // scroll optimization
  useEffect(() => {
    let t: number | null = null;
    const onScroll = () => {
      setReduceVisuals(true);
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => {
        setReduceVisuals(false);
        t = null;
      }, 140);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (t) window.clearTimeout(t);
    };
  }, []);

  // lottie visibility
  useEffect(() => {
    if (typeof window === 'undefined') {
      setVisibleLottie(true);
      return;
    }
    const hero = document.querySelector('[data-hero-observe]');
    if (!hero) {
      setVisibleLottie(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisibleLottie(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(hero);
    return () => obs.disconnect();
  }, []);

  // entrance animations
  useEffect(() => {
    pageControls.start({
      opacity: [0, 1],
      y: [-8, 0],
      scale: [0.997, 1],
      transition: { duration: 0.56, ease: 'easeOut' },
    });
    safePlaySound(SFX.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentPage === 2) {
      safePlaySound(SFX.portal);
      portalControls.start({
        scale: [0.88, 1.02, 1],
        opacity: [0, 1, 0.98],
        rotate: [0, 10, 0],
        transition: { duration: 1.0, ease: 'easeInOut' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // pageflip controls
  const goToNext = useCallback(() => {
    safePlaySound(SFX.whoosh);
    try {
      if (pageFlip.current?.flipNext) {
        pageFlip.current.flipNext();
      } else {
        setPage((p) => Math.min(p + 1, 9999));
      }
    } catch {
      setPage((p) => p + 1);
    }
  }, [SFX]);

  const goToPrev = useCallback(() => {
    safePlaySound(SFX.whoosh);
    try {
      if (pageFlip.current?.flipPrev) {
        pageFlip.current.flipPrev();
      } else {
        setPage((p) => Math.max(p - 1, 0));
      }
    } catch {
      setPage((p) => Math.max(p - 1, 0));
    }
  }, [SFX]);

  // robust portal nav
  const triggerPortalNav = useCallback(
    (to: string) => {
      setPortalActive(true);
      safePlaySound(SFX.portal);
      try {
        overlayControls.set({ opacity: 0, scale: 0.86 });
        overlayControls.start({
          scale: [0.86, 1.02, 1],
          opacity: [0, 0.9, 1],
          transition: { duration: 0.42, ease: 'circOut' },
        });
      } catch {
        // ignore
      }
      setTimeout(() => {
        try {
          router.push(to);
        } catch {
          window.location.href = to;
        }
      }, 110);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overlayControls, router, SFX.portal]
  );

  // CTA helper
  const CTA = ({ children, href, burn }: { children: React.ReactNode; href: string; burn?: boolean }) => {
    const { props: actProps } = useReliableAction(() => triggerPortalNav(href), { lockMs: 540 });
    return (
      <button {...(actProps as any)} className={`btn ${burn ? 'btn-burn' : 'btn-gold'}`} aria-label={`Go to ${href}`} onFocus={(e) => (e.currentTarget.style.outline = 'none')}>
        <span className="btn-inner">{children}</span>
      </button>
    );
  };

  /* ------------------------------ Subcomponents --------------------------- */
  const NebulaBackdrop = () => (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
        background:
          `radial-gradient(30% 30% at 8% 20%, rgba(255,215,0,0.06), transparent 12%),` +
          `radial-gradient(40% 35% at 84% 10%, rgba(255,242,200,0.04), transparent 18%),` +
          `linear-gradient(180deg, rgba(5,5,10,0.72), rgba(10,8,16,0.86))`,
        mixBlendMode: 'screen',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
      }}
    >
      <motion.div
        initial={{ opacity: 0.08 }}
        animate={{ opacity: reduceVisuals ? 0.08 : [0.08, 0.2, 0.08] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          width: '125%',
          height: '125%',
          transformOrigin: 'center',
          filter: isMobile ? 'blur(12px)' : 'blur(28px)',
          background:
            'radial-gradient(40% 30% at 50% 50%, rgba(255,215,0,0.06), rgba(255,215,0,0.02) 35%, transparent 60%)',
        }}
      />
    </div>
  );

  const GoldenBubble = ({ x, y, size = 120, intensity = 0.22 }: { x: number | string; y: number | string; size?: number; intensity?: number }) => {
    const convert = (v: number | string) => (typeof v === 'number' ? `${v}px` : v);
    const style: React.CSSProperties = {
      position: 'absolute',
      left: convert(x),
      top: convert(y),
      width: size,
      height: size,
      borderRadius: '50%',
      transform: 'translate(-50%,-50%)',
      background: `radial-gradient(circle at 35% 35%, rgba(${GOLD_RGBA},${0.98}), rgba(255,242,180,0.82) 35%, rgba(255,215,0,0.06) 60%)`,
      boxShadow: `0 40px ${Math.max(80, size / 1.2)}px rgba(${GOLD_RGBA}, ${intensity})`,
      filter: 'saturate(120%)',
      pointerEvents: 'none',
      zIndex: 2,
      maxWidth: '40vw',
    };
    return <motion.div initial={{ opacity: 0.2 }} animate={{ opacity: reduceVisuals ? 0.12 : [0.12, 0.9, 0.18] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }} style={style} />;
  };

  const ParticleField = () => {
    return (
      <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
        {particles.map((p, i) => {
          const isFront = p.layer === 'front';
          const blur = isFront ? Math.max(18, p.size / 1.25) : Math.max(6, p.size / 1.6);
          const boxShadow = isFront
            ? `0 0 ${Math.max(24, p.size * 1.4)}px rgba(${GOLD_RGBA}, ${(p.opacity * 0.32).toFixed(2)})`
            : `0 0 ${Math.max(6, p.size / 1.2)}px rgba(${GOLD_RGBA}, ${(p.opacity * 0.12).toFixed(2)})`;

          return (
            <motion.div
              key={p.id}
              initial={{ x: p.x, y: p.y, opacity: 0.04, scale: 0.72, rotate: p.rotation }}
              animate={{
                x: [p.x, p.x + p.sway * 0.14, p.x],
                y: [p.y, p.y - (isMobile ? 20 + p.size : 30 + p.size * 1.6), p.y],
                opacity: reduceVisuals ? 0.12 : [p.opacity * 0.2, p.opacity, p.opacity * 0.36],
                rotate: [p.rotation, p.rotation + 120, p.rotation],
                scale: isFront ? [0.95, 1.03, 0.98] : [0.85, 1, 0.92],
              }}
              transition={{
                duration: 8 + (i % 7) * 1.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: (i % 6) * 0.08,
              }}
              style={{
                position: 'absolute',
                width: p.size,
                height: p.size,
                borderRadius: 999,
                background: `radial-gradient(circle at 30% 30%, ${p.hue}, rgba(255,255,255,0.04) 55%)`,
                boxShadow,
                mixBlendMode: 'screen',
                filter: `blur(${blur}px)`,
                willChange: 'transform, opacity',
                zIndex: p.layer === 'front' ? 8 : 1,
                maxWidth: '40vw',
              }}
            />
          );
        })}
      </div>
    );
  };

  const Flame = ({ size = 32, intensity = 1.0 }: { size?: number; intensity?: number }) => {
    const w = Math.max(18, size);
    const h = Math.max(24, Math.round(size * 1.3));
    const flameStyle: React.CSSProperties = {
      width: w,
      height: h,
      position: 'relative',
      display: 'inline-block',
      transformOrigin: 'center bottom',
      pointerEvents: 'none',
    };
    const uid = `flame-${Math.round(Math.random() * 1e6)}`;
    const styleString = `
      @keyframes ${uid}-flicker {
        0% { transform: translateY(0) scaleY(1); opacity: 0.92; filter: blur(0.6px) }
        30% { transform: translateY(-4%) scaleY(1.05); opacity: 1; filter: blur(0.4px) }
        60% { transform: translateY(0) scaleY(0.98); opacity: 0.86; filter: blur(0.9px) }
        100% { transform: translateY(0) scaleY(1); opacity: 0.92; filter: blur(0.6px) }
      }
      .${uid} .f-layer {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 0;
        border-radius: 50% 40% 20% 40% / 70% 60% 50% 40%;
        filter: saturate(140%);
        will-change: transform, opacity, filter;
      }
      .${uid} .f-1 { width: ${Math.round(w * 0.82)}px; height: ${Math.round(h * 0.92)}px; background: radial-gradient(circle at 45% 25%, rgba(${GOLD_RGBA},0.95), rgba(255,130,0,0.08) 46%, rgba(255,255,255,0) 78%); opacity: ${0.92 * intensity}; animation: ${uid}-flicker ${800 + Math.round(200 * Math.random())}ms infinite ease-in-out; transform-origin: center bottom; }
      .${uid} .f-2 { width: ${Math.round(w * 0.56)}px; height: ${Math.round(h * 0.66)}px; background: radial-gradient(circle at 50% 18%, rgba(255,210,80,0.98), rgba(255,120,20,0.06) 45%, rgba(255,255,255,0) 68%); opacity: ${0.86 * intensity}; animation: ${uid}-flicker ${900 + Math.round(300 * Math.random())}ms infinite ease-in-out; transform-origin: center bottom; }
      .${uid} .f-3 { width: ${Math.round(w * 0.36)}px; height: ${Math.round(h * 0.44)}px; background: radial-gradient(circle at 50% 10%, rgba(255,255,255,0.95), rgba(255,220,70,0.04) 40%); opacity: ${0.8 * intensity}; animation: ${uid}-flicker ${700 + Math.round(200 * Math.random())}ms infinite ease-in-out; transform-origin: center bottom; }
    `;
    return (
      <span aria-hidden className={uid} style={flameStyle}>
        <style>{styleString}</style>
        <span className="f-layer f-1" />
        <span className="f-layer f-2" />
        <span className="f-layer f-3" />
      </span>
    );
  };

  const PortalOverlay = () => (
    <motion.div
      data-portal-overlay
      className={portalActive ? 'active' : ''}
      initial={{ opacity: 0, scale: 0.78 }}
      animate={overlayControls}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: portalActive ? 9999 : -1,
        display: portalActive ? undefined : 'none',
        pointerEvents: portalActive ? 'auto' : 'none',
        background: `radial-gradient(circle at 50% 45%, rgba(${GOLD_RGBA},0.75), rgba(0,0,0,0.0) 35%), linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.6))`,
        backdropFilter: isMobile ? 'none' : 'blur(8px) saturate(120%)',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div
          initial={{ scale: 0.6, rotateY: 0, opacity: 0 }}
          animate={{ scale: [0.6, 1.35, 0.96], rotateY: [0, 24, 0], opacity: [0, 1, 0.95] }}
          transition={{ duration: 0.55, ease: 'circOut' }}
          style={{
            width: 'min(64vw, 720px)',
            height: 'min(64vw, 720px)',
            borderRadius: '28%',
            background: `conic-gradient(from 30deg, rgba(${GOLD_RGBA},0.95), rgba(255,240,180,0.75) 20%, rgba(120,111,16,0.12) 40%, rgba(0,0,0,0.05) 60%)`,
            boxShadow: `0 40px 200px rgba(${GOLD_RGBA}, 0.22), inset 0 0 120px rgba(${GOLD_RGBA}, 0.18)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ width: '72%', height: '72%' }}>
            {visibleLottie ? <Lottie animationData={portalLottie} play loop style={{ width: '100%', height: '100%' }} /> : null}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // close overlay with escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && portalActive) {
        setPortalActive(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [portalActive]);

  // reliable actions for major buttons
  const prevAction = useReliableAction(goToPrev, { lockMs: 420 });
  const nextAction = useReliableAction(goToNext, { lockMs: 420 });
  const openDashboardAction = useReliableAction(() => triggerPortalNav('/dashboard'), { lockMs: 520 });
  const newStoryAction = useReliableAction(() => triggerPortalNav('/dashboard/new-story'), { lockMs: 520 });
  const galleryAction = useReliableAction(() => triggerPortalNav('/gallery'), { lockMs: 520 });
  const subscriptionAction = useReliableAction(() => triggerPortalNav('/subscribe'), { lockMs: 520 });
  const signupAction = useReliableAction(() => triggerPortalNav('/signup'), { lockMs: 520 });
  const contactSalesAction = useReliableAction(() => triggerPortalNav('/contact'), { lockMs: 520 });

  /* ------------------------------ HTML / JSX -------------------------------- */
  return (
    <StoryContext.Provider value={{ currentPage, setPage }}>
      <div
        ref={rootRef}
        style={{
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'auto',
          background: BACKGROUND_BLACK,
          color: ACCENT_WHITE,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          touchAction: 'pan-y',
          overscrollBehavior: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Global styles */}
        <style jsx global>{`
          :root {
            --gold: ${GOLD_HEX};
            --gold-rgb: ${GOLD_RGBA};
            --bg-black: ${BACKGROUND_BLACK};
            --text-white: ${ACCENT_WHITE};
            --glass: rgba(255,255,255,0.03);
          }

          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700;800&family=Inter:wght@300;400;600;700;800&display=swap');

          html, body, #__next { height: 100%; margin: 0; padding: 0; background: var(--bg-black); color: var(--text-white); }
          *, *::before, *::after { box-sizing: border-box; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
          a, button { color: inherit; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; }
          img, svg, canvas, video { max-width: 100%; height: auto; display: block; }

          .smooth-transition * { transition: all 360ms cubic-bezier(.2,.9,.25,1); }
          * { transition-property: background-color, color, transform, opacity, box-shadow; }

          .glow-gold { text-shadow: 0 8px 36px rgba(var(--gold-rgb), 0.18), 0 2px 0 rgba(0,0,0,0.6); }
          .glow-subtle { text-shadow: 0 6px 20px rgba(var(--gold-rgb), 0.12); }

          .container { max-width: 1340px; margin: 0 auto; padding: clamp(12px, 3.2vw, 28px); box-sizing: border-box; }
          .card {
            background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01));
            border: 1px solid rgba(255,255,255,0.04);
            border-radius: 14px;
            box-shadow: 0 8px 48px rgba(0,0,0,0.6), inset 0 0 44px rgba(var(--gold-rgb),0.02);
            min-width: 0;
          }

          /* buttons */
          .btn, button { border-radius: 10px; border: 1px solid rgba(255,255,255,0.06); background: transparent; cursor: pointer; padding: 0; }
          .btn .btn-inner { padding: 10px 14px; border-radius: 10px; font-weight: 800; color: #0b0b0d; background: linear-gradient(90deg, rgba(var(--gold-rgb),0.98), rgba(255,242,180,0.92)); box-shadow: 0 14px 50px rgba(var(--gold-rgb), 0.18), 0 2px 8px rgba(0,0,0,0.6); }

          .btn::after {
            content: '';
            position: absolute;
            inset: -6px;
            border-radius: 12px;
            box-shadow: 0 12px 60px rgba(var(--gold-rgb), 0.22), inset 0 0 12px rgba(var(--gold-rgb), 0.10);
            opacity: 0.95;
            z-index: -1;
            pointer-events: none;
          }

          .btn-gold .btn-inner { background: linear-gradient(180deg, rgba(var(--gold-rgb),0.98), rgba(255,242,180,0.92)); color: #0b0b0d; }
          .btn-burn { position: relative; overflow: visible; }
          .btn-burn .btn-inner { background: linear-gradient(90deg, rgba(255,245,160,1), rgba(255,235,120,0.98)); color: #0b0b0d; }
          .btn-burn::before, .btn-burn::after {
            content: '';
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            pointer-events: none;
            z-index: -2;
            border-radius: 999px;
            filter: blur(12px);
            opacity: 0.92;
          }
          .btn-burn::before {
            bottom: -8px;
            width: calc(100% + 40px);
            height: 32px;
            background: radial-gradient(ellipse at center, rgba(var(--gold-rgb),0.32) 0%, rgba(var(--gold-rgb),0.12) 40%, rgba(255,255,255,0) 70%);
            animation: burn-flicker 1600ms infinite linear;
          }
          .btn-burn::after {
            bottom: -20px;
            width: calc(100% + 60px);
            height: 56px;
            background: radial-gradient(ellipse at center, rgba(var(--gold-rgb),0.18) 0%, rgba(255,242,180,0.06) 35%, rgba(255,255,255,0) 70%);
            animation: burn-ember 2200ms infinite linear;
            filter: blur(18px);
          }

          @keyframes burn-flicker {
            0% { transform: translateX(-50%) scaleX(0.98); opacity: 0.85; filter: blur(10px); }
            20% { transform: translateX(-50%) scaleX(1.03); opacity: 0.95; filter: blur(8px); }
            50% { transform: translateX(-50%) scaleX(0.96); opacity: 0.82; filter: blur(12px); }
            80% { transform: translateX(-50%) scaleX(1.06); opacity: 0.98; filter: blur(7px); }
            100% { transform: translateX(-50%) scaleX(0.98); opacity: 0.86; filter: blur(10px); }
          }
          @keyframes burn-ember {
            0% { transform: translateX(-50%) translateY(0) scaleX(1); opacity: 0.55; }
            40% { transform: translateX(-50%) translateY(-8px) scaleX(1.06); opacity: 0.88; }
            70% { transform: translateX(-50%) translateY(0) scaleX(0.98); opacity: 0.6; }
            100% { transform: translateX(-50%) translateY(0) scaleX(1); opacity: 0.55; }
          }

          .pageflip-wrap { position: relative; border-radius: 20px; }
          .pageflip-wrap::before, .pageflip-wrap::after {
            content: '';
            position: absolute;
            inset: -18px;
            z-index: -2;
            border-radius: 22px;
            background: radial-gradient(ellipse at center, rgba(var(--gold-rgb),0.12), rgba(0,0,0,0) 60%);
            filter: blur(36px);
            transform: translateZ: 0;
            pointer-events: none;
            opacity: 0.9;
          }
          .pageflip-wrap::after {
            inset: -36px;
            background: radial-gradient(ellipse at center, rgba(var(--gold-rgb),0.10), rgba(0,0,0,0) 62%);
            filter: blur(68px);
            animation: pageburn-floater 2600ms infinite linear;
          }
          @keyframes pageburn-floater {
            0% { transform: translateY(0) scale(1); opacity: 0.82; }
            40% { transform: translateY(-8px) scale(1.02); opacity: 1; }
            80% { transform: translateY(0) scale(0.98); opacity: 0.8; }
            100% { transform: translateY(0) scale(1); opacity: 0.82; }
          }

          .reduce-visuals .pageflip-wrap::before,
          .reduce-visuals .pageflip-wrap::after,
          .reduce-visuals .oval-outline::before { opacity: 0.25 !important; filter: none !important; }

          .glass-outline { border: 1px solid rgba(255,255,255,0.04); padding: 8px 10px; border-radius: 12px; background: rgba(255,255,255,0.02); font-weight: 700; display: inline-flex; gap: 8px; align-items: center; }
          .muted { color: rgba(255,255,255,0.84); }
          .icon-circle { height: 56px; width: 56px; border-radius: 12px; display: grid; place-items: center; background: linear-gradient(180deg, rgba(var(--gold-rgb),0.98), rgba(255,240,180,0.92)); box-shadow: 0 8px 26px rgba(var(--gold-rgb), 0.12); }

          .hero-grid { display: grid; grid-template-columns: 1fr 420px; gap: 22px; align-items: start; }
          .features-3col { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
          .pricing-grid-4 { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 12px; align-items: start; }
          .community-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; align-items: start; }

          .oval-outline { position: relative; overflow: visible; }
          .oval-outline::before {
            content: '';
            pointer-events: none;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: calc(100% + 42px);
            height: calc(100% + 28px);
            border-radius: 48% / 60%;
            border: 1px solid rgba(var(--gold-rgb), 0.28);
            box-shadow: 0 6px 40px rgba(var(--gold-rgb), 0.12), inset 0 0 28px rgba(var(--gold-rgb), 0.06);
            z-index: -1;
            filter: drop-shadow(0 10px 40px rgba(var(--gold-rgb),0.08));
          }

          /* important: prevent overflow from children */
          .card, .hero-grid > div, .features-3col > * , .pricing-grid-4 > * , .community-grid > * { min-width: 0; word-break: break-word; overflow-wrap: break-word; }

          /* responsive breakpoints */
          @media (max-width: 920px) {
            .hero-grid { grid-template-columns: 1fr; gap: 16px; }
            .features-3col { grid-template-columns: 1fr; gap: 12px; }
            .pricing-grid-4 { grid-template-columns: 1fr; gap: 12px; }
            .community-grid { grid-template-columns: 1fr; }
            .oval-outline::before { box-shadow: 0 6px 26px rgba(var(--gold-rgb),0.09); border: 1px solid rgba(var(--gold-rgb),0.18); filter: none; width: calc(100% + 16px); height: calc(100% + 14px); }
            .container { padding: 14px; }
            .btn .btn-inner { padding: 10px 12px; font-size: 15px; }
            .btn-burn::before, .btn-burn::after { filter: blur(10px); opacity: 0.7; }
            .pageflip-wrap::before, .pageflip-wrap::after { filter: blur(30px); opacity: 0.7; }
            .card { border-radius: 12px; padding: 12px; }
          }

          @media (max-width: 520px) {
            .pricing-grid-4 { gap: 12px; }
            .container { padding-left: 12px; padding-right: 12px; }
            .pageflip-wrap { padding: 6px !important; border-radius: 12px; }
          }
        `}</style>

        {/* Backdrops & particle field */}
        <NebulaBackdrop />
        <ParticleField />

        {/* Golden bubbles - use percentage positions for mobile friendliness */}
        <GoldenBubble x={'6%'} y={isMobile ? 72 : 120} size={isMobile ? 86 : 110} intensity={0.26} />
        <GoldenBubble x={'84%'} y={isMobile ? 160 : 220} size={isMobile ? 110 : 160} intensity={0.22} />
        <GoldenBubble x={'72%'} y={isMobile ? 420 : 520} size={isMobile ? 86 : 120} intensity={0.18} />

        {/* MAIN */}
        <main style={{ position: 'relative', zIndex: 10 }} className={`container smooth-transition ${reduceVisuals ? 'reduce-visuals' : ''}`}>

          {/* HERO */}
          <section data-hero-observe className="oval-outline" style={{ padding: isMobile ? '16px 0' : '36px 0 18px' }}>
            <div className="hero-grid">
              <div>
                <motion.h1 initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{ fontSize: 'clamp(26px, 7.5vw, 46px)', lineHeight: 1.02, fontWeight: 900, margin: 0 }}>
                  <span style={{ color: GOLD_HEX }} className="glow-gold">The Story Club</span>
                </motion.h1>

                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.08 }} style={{ fontSize: 15, marginTop: 10, color: 'rgba(255,255,255,0.92)', maxWidth: '100%' }}>
                  AI-first story studio — generate panels, images & full video, score music, auto-voice narration, export PDFs, and produce multilingual stories. Start with a single idea and publish an AI-generated short or movie.
                </motion.p>

                <div style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button {...(newStoryAction.props as any)} className="btn btn-burn" aria-label="Begin Now" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <span className="btn-inner">Begin Now</span>
                    <span style={{ marginLeft: 6 }} aria-hidden><Flame size={20} intensity={1.1} /></span>
                  </button>

                  <button {...(openDashboardAction.props as any)} className="btn btn-gold glass-outline" style={{ borderRadius: 12 }}>
                    <span style={{ fontWeight: 800 }}>Open Dashboard</span>
                  </button>

                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <div className="glass-outline" style={{ padding: 8, borderRadius: 12 }}>
                      <div style={{ fontWeight: 900, fontSize: 14, color: GOLD_HEX }} className="glow-gold">Beta</div>
                      <div style={{ fontSize: 12, opacity: 0.78 }}>Generative movie export</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div className="glass-outline" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Sparkles size={18} style={{ color: GOLD_HEX }} />
                    <div style={{ fontSize: 13 }}>AI Story-Buddy</div>
                  </div>

                  <div className="glass-outline" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Video size={18} style={{ color: GOLD_HEX }} />
                    <div style={{ fontSize: 13 }}>Image & Video Generation</div>
                  </div>

                  <div className="glass-outline" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Music size={18} style={{ color: GOLD_HEX }} />
                    <div style={{ fontSize: 13 }}>Music & Voice Reading</div>
                  </div>
                </div>
              </div>

              {/* Right column - visual card */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'start' }}>
                <motion.div initial={{ rotateY: 6, rotateX: -3, scale: 0.995, opacity: 0 }} animate={{ rotateY: 0, rotateX: 0, scale: 1, opacity: 1 }} transition={{ duration: 0.68 }} className="card futuristic" style={{ width: '100%', maxWidth: 520, borderRadius: 18, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                    <motion.div initial={{ scale: 1.02 }} animate={{ scale: reduceVisuals ? 1.0 : [1.02, 1, 1.01] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }} style={{ position: 'absolute', inset: '-14%', background: `radial-gradient(40% 30% at 20% 20%, rgba(${GOLD_RGBA},0.06), transparent 22%), conic-gradient(from 120deg at 70% 30%, rgba(255,250,210,0.03), rgba(${GOLD_RGBA},0.02))`, filter: isMobile ? 'blur(8px)' : 'blur(12px)', opacity: 1 }} />
                  </div>

                  <div style={{ position: 'relative', zIndex: 2, display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div style={{ height: 56, width: 56, borderRadius: 12, display: 'grid', placeItems: 'center', background: `linear-gradient(180deg, rgba(${GOLD_RGBA},0.98), rgba(255,240,180,0.92))`, boxShadow: `0 8px 26px rgba(${GOLD_RGBA},0.12)` }}>
                        <BookOpen size={24} color="#0b0b0d" />
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 900 }}>The Story Club Studio</div>
                        <div style={{ fontSize: 13, opacity: 0.88 }}>Panel creation • AI co-writer • exports</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.84)' }}>Progress</div>
                        <div style={{ marginTop: 8, height: 10, borderRadius: 999, background: 'rgba(255,255,255,0.03)' }}>
                          <motion.div initial={{ width: '28%' }} animate={{ width: '72%' }} transition={{ duration: 2.2, ease: 'easeInOut' }} style={{ height: '100%', borderRadius: 999, background: `linear-gradient(90deg, rgba(${GOLD_RGBA},0.98), rgba(255,242,180,0.92))`, boxShadow: `0 8px 26px rgba(${GOLD_RGBA},0.14)` }} />
                        </div>
                      </div>

                      <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.03)' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 2L15 8H9L12 2Z" stroke={GOLD_HEX} strokeWidth={1.1} strokeLinecap="round" /></svg>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                      <button {...(newStoryAction.props as any)} className="btn btn-burn important-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, display: 'grid', placeItems: 'center', background: `linear-gradient(180deg, rgba(${GOLD_RGBA},0.98), rgba(255,242,180,0.92))` }} aria-hidden>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 4L20 10L12 20L4 10L12 4Z" fill="#0B0B0D" /></svg>
                        </div>
                        <div style={{ fontWeight: 900 }}>Forge Panel</div>
                        <div aria-hidden style={{ marginLeft: 6 }}><Flame size={20} intensity={1.05} /></div>
                      </button>

                      <button {...(galleryAction.props as any)} className="glass-outline" style={{ padding: '8px 12px', borderRadius: 12 }}>
                        Gallery
                      </button>
                    </div>

                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* PAGEFLIP */}
          <section style={{ padding: isMobile ? '14px 0 22px' : '18px 0 28px' }}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: GOLD_HEX, fontWeight: 900, fontSize: 18 }} className="glow-gold">Live Panels</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.86)' }}>Flip through previews — export images, shorts, or full AI-generated movies.</div>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button {...(prevAction.props as any)} className="btn btn-gold" style={{ padding: 10, borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.03)' }}>
                    <BookOpen size={18} />
                  </button>

                  <button {...(nextAction.props as any)} className="btn btn-burn" style={{ padding: 10, borderRadius: 12 }}>
                    <BookOpen size={18} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.995 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7 }}
                  className="pageflip-wrap card"
                  style={{
                    width: '100%',
                    maxWidth: isMobile ? '100%' : 980,
                    borderRadius: isMobile ? 12 : 20,
                    overflow: 'hidden',
                    padding: isMobile ? 6 : 8,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
                    boxShadow: `0 30px 120px rgba(0,0,0,0.6)`,
                    border: '1px solid rgba(255,255,255,0.03)',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ position: 'relative', width: '100%', overflow: 'hidden', boxSizing: 'border-box', borderRadius: 16 }}>
                    {/* Note: flipSize.width is computed to respect mobile width; we still pass numeric width to PageFlip */}
                    <PageFlip
                      ref={pageFlip}
                      width={flipSize.width}
                      height={flipSize.height}
                      onFlip={(e: any) => setPage(e.data)}
                      className="pageflip-inner"
                      style={{ width: '100%', maxWidth: '100%', height: flipSize.height, boxSizing: 'border-box' }}
                    >
                      {/* Slide 1 */}
                      <div style={{ boxSizing: 'border-box', padding: 24, width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#07070b' }}>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${backgrounds.panel1})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.44, filter: 'saturate(1.05) contrast(1.02)' }} />
                        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 12 }}>
                          <h3 style={{ color: GOLD_HEX, fontSize: 26, margin: 0, fontWeight: 900 }} className="glow-gold">Welcome to The Story Club</h3>
                          <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.92)', maxWidth: 720 }}>AI-first studio — create a panel, render an image, add sound and voice, then export a short or AI-generated movie.</p>
                          <div style={{ marginTop: 12 }}>
                            <button {...(newStoryAction.props as any)} className="btn btn-burn important-cta">
                              <span className="btn-inner">Create First Panel</span>
                              <span aria-hidden style={{ marginLeft: 8 }}><Flame size={18} intensity={1.02} /></span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Slide 2 */}
                      <div style={{ boxSizing: 'border-box', padding: 24, width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#0b0a0f' }}>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${backgrounds.panel2})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.42, filter: 'saturate(1.02) contrast(1.02)' }} />
                        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 12 }}>
                          <h3 style={{ color: GOLD_HEX, fontSize: 26, margin: 0, fontWeight: 900 }} className="glow-gold">Open Your Dashboard</h3>
                          <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.92)', maxWidth: 720 }}>Continue a story, tune AI beats, or produce exports — images, PDF storybooks, MP4 shorts, or full AI-generated movies.</p>
                          <div style={{ marginTop: 12 }}>
                            <button {...(openDashboardAction.props as any)} className="btn btn-burn important-cta">
                              <span className="btn-inner">Open Dashboard</span>
                              <span aria-hidden style={{ marginLeft: 8 }}><Flame size={18} intensity={1.02} /></span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Slide 3 */}
                      <div style={{ boxSizing: 'border-box', padding: 24, width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#0a0408' }}>
                        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${backgrounds.panel3})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.4, transform: 'scale(1.02)' }} />
                        <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 12 }}>
                          <h3 style={{ color: GOLD_HEX, fontSize: 26, margin: 0, fontWeight: 900 }} className="glow-gold">Make the First Panel</h3>
                          <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.92)', maxWidth: 720 }}>Describe a character and mood — The Story Club generates images, animates them, adds a soundtrack and narration, and prepares exports for web and mobile.</p>
                          <div style={{ marginTop: 12 }}>
                            <button {...(newStoryAction.props as any)} className="btn btn-burn important-cta">
                              <span className="btn-inner">Craft it Now</span>
                              <span aria-hidden style={{ marginLeft: 8 }}><Flame size={18} intensity={1.02} /></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </PageFlip>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </section>
{/* FEATURES */}
<section style={{ padding: '12px 0 36px' }}>
  <div style={{ display: 'grid', gap: 16 }}>
    <motion.h2
      initial={{ y: 6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      style={{ color: GOLD_HEX, fontSize: 22, fontWeight: 900 }}
      className="glow-gold"
    >
      What the platform does
    </motion.h2>

    {/* Features Grid */}
    <div
      className="features-3col"
      style={{
        marginTop: 6,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 12,
      }}
    >
      {[
        {
          icon: <Sparkles size={46} color={GOLD_HEX} />,
          title: 'AI Story-Buddy',
          desc: 'Panel-by-panel guidance, scene ideas, pacing and beat generation.',
        },
        {
          icon: <BookOpen size={46} color={GOLD_HEX} />,
          title: 'Illustrations & Images',
          desc: 'AI-rendered artwork per scene with style options.',
        },
        {
          icon: <Users size={46} color={GOLD_HEX} />,
          title: 'Collaboration',
          desc: 'Invite collaborators and co-author stories in real-time.',
        },
      ].map((f, i) => (
        <FeatureCard
          key={i}
          icon={f.icon}
          title={f.title}
          desc={f.desc}
          bgImg={backgrounds.feature[i]!}
          delay={i * 0.1}
        />
      ))}
    </div>

    {/* Platform Capabilities */}
    <div className="card" style={{ padding: 12, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div className="icon-circle">
          <Sparkles size={20} color="#0b0b0d" />
        </div>
        <div>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Platform capabilities</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            Everything you need to craft, sound-design, animate, and publish.
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 10,
        }}
      >
        <div className="muted">
          • AI-generated images and videos<br />
          • AI-generated full-length short movies (export-ready)<br />
          • Original music & theme generation per story
        </div>
        <div className="muted">
          • Voice narration (multi-voice) & TTS exports<br />
          • PDF export, frame-by-frame storyboards<br />
          • Multilingual generation & dubbing
        </div>
      </div>
    </div>
  </div>
</section>

          {/* PRICING */}
          <section style={{ padding: '20px 0 48px' }}>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: GOLD_HEX, fontSize: 20, fontWeight: 900 }} className="glow-gold">Publish & Monetize</div>
                <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.9)' }}>From family stories to AI-generated movies — choose a plan that fits your scale and needs.</div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button {...(subscriptionAction.props as any)} className="btn btn-gold">
                  <span className="btn-inner">See Plans</span>
                </button>

                <button {...(signupAction.props as any)} className="glass-outline" style={{ borderRadius: 12 }}>
                  Start Free
                </button>
              </div>
            </div>

            <div style={{ marginTop: 18 }} className="pricing-grid-4">
              {/* Wanderer */}
              <motion.div whileHover={{ y: -6 }} className="card" style={{ padding: 16, borderRadius: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>Wanderer</div>
                    <div style={{ fontSize: 13, opacity: 0.86 }}>Free • Try the basics</div>
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 18, color: 'rgba(255,255,255,0.95)' }}>Free</div>
                </div>

                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13 }}>• 10 stories / month</div>
                  <div style={{ fontSize: 13 }}>• 3 short exports</div>
                  <div style={{ fontSize: 13 }}>• Basic music & voice</div>

                  <div style={{ marginTop: 8 }}>
                    <button {...(subscriptionAction.props as any)} className="btn btn-gold" style={{ width: '100%' }}>
                      <span className="btn-inner">Try Wanderer</span>
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Taleweaver */}
              <motion.div whileHover={{ y: -6 }} className="card" style={{ padding: 16, borderRadius: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>Taleweaver</div>
                    <div style={{ fontSize: 13, opacity: 0.86 }}>Indie creators</div>
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 22, color: GOLD_HEX }}>$29.99</div>
                </div>

                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13 }}>• 50 stories / month</div>
                  <div style={{ fontSize: 13 }}>• 10 short exports</div>
                  <div style={{ fontSize: 13 }}>• High-res images & audio</div>

                  <div style={{ marginTop: 8 }}>
                    <button {...(subscriptionAction.props as any)} className="btn btn-burn" style={{ width: '100%' }}>
                      <span className="btn-inner">Choose Taleweaver</span>
                      <span aria-hidden style={{ marginLeft: 8 }}><Flame size={16} intensity={1.0} /></span>
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Chronicle */}
              <motion.div whileHover={{ y: -8 }} className="card" style={{
                padding: 18, borderRadius: 16,
                background: `linear-gradient(180deg, ${GOLD_HEX}, rgba(255,240,180,0.95))`,
                color: '#0b0b0d',
                border: 'none',
                boxShadow: `0 30px 120px rgba(${GOLD_RGBA}, 0.18), 0 10px 40px rgba(0,0,0,0.25)`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 20 }}>Chronicle</div>
                    <div style={{ fontSize: 13, opacity: 0.95 }}>Most creators choose this</div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 900, fontSize: 20 }}>$69.99</div>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>per month</div>
                  </div>
                </div>

                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>Includes</div>
                  <div style={{ fontSize: 13 }}>• 200 stories / month</div>
                  <div style={{ fontSize: 13 }}>• 30 short exports with advanced animation</div>
                  <div style={{ fontSize: 13 }}>• Priority support & early feature access</div>
                  <div style={{ fontSize: 13 }}>• Commercial license for published shorts</div>

                  <div style={{ marginTop: 12 }}>
                    <button {...(subscriptionAction.props as any)} style={{ width: '100%', padding: '12px 14px', borderRadius: 12, background: '#FFFFFF', color: '#0b0b0d', fontWeight: 900, border: 'none', boxShadow: `0 12px 40px rgba(0,0,0,0.22)` }}>
                      Get Chronicle
                    </button>
                  </div>

                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 10 }}>
                    <div style={{ padding: '6px 10px', borderRadius: 999, background: 'rgba(0,0,0,0.06)', fontWeight: 800, fontSize: 12 }}>Most Popular</div>
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.7)', alignSelf: 'center' }}>Best balance of features</div>
                  </div>
                </div>
              </motion.div>

              {/* Mythforger */}
              <motion.div whileHover={{ y: -6 }} className="card" style={{ padding: 16, borderRadius: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>Mythforger</div>
                    <div style={{ fontSize: 13, opacity: 0.86 }}>Teams & studios</div>
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 22, color: 'rgba(255,255,255,0.95)' }}>$129.99</div>
                </div>

                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13 }}>• Unlimited stories</div>
                  <div style={{ fontSize: 13 }}>• Unlimited exports & priority rendering</div>
                  <div style={{ fontSize: 13 }}>• Dedicated account manager</div>
                  <div style={{ fontSize: 13 }}>• AI-generated movie export (up to 3 min) + studio-grade audio</div>
                  <div style={{ fontSize: 13 }}>• Multilanguage dubbing & PDF bulk export</div>

                  <div style={{ marginTop: 8 }}>
                    <button {...(contactSalesAction.props as any)} className="btn btn-gold" style={{ width: '100%' }}>
                      <span className="btn-inner">Contact Sales</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* extras */}
            <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div className="card" style={{ padding: 12, borderRadius: 12 }}>
                <div style={{ fontWeight: 900 }}>Save 20% annually</div>
                <div style={{ fontSize: 13, marginTop: 6 }} className="muted">Choose annual billing for better savings.</div>
              </div>

              <div className="card" style={{ padding: 12, borderRadius: 12 }}>
                <div style={{ fontWeight: 900 }}>Custom add-ons</div>
                <div style={{ fontSize: 13, marginTop: 6 }} className="muted">Higher-res outputs, soundtrack packages, advanced animation kits, and studio integrations.</div>
              </div>
            </div>
          </section>

          {/* COMMUNITY & ROADMAP */}
          <section style={{ padding: '18px 0 48px' }}>
            <div style={{ display: 'grid', gap: 16 }}>
              <motion.h2 initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }} style={{ color: GOLD_HEX, fontSize: 20, fontWeight: 900 }} className="glow-gold">Community Voices</motion.h2>

              <div className="community-grid">
                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 900, wordBreak: 'break-word' }}>“The Story Club turned our short into a finished short within an afternoon.”</div>
                  <div style={{ marginTop: 8 }} className="muted">— Maya R., indie animator</div>
                </div>

                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 900, wordBreak: 'break-word' }}>“Collab mode is great for family storytime.”</div>
                  <div style={{ marginTop: 8 }} className="muted">— Ahmed T., parent & teacher</div>
                </div>

                <div className="card" style={{ padding: 12 }}>
                  <div style={{ fontWeight: 900, wordBreak: 'break-word' }}>“Portal animation and sound design sell the experience — big fan.”</div>
                  <div style={{ marginTop: 8 }} className="muted">— Lena K., content creator</div>
                </div>
              </div>

              <div className="card" style={{ padding: 12 }}>
                <div style={{ fontWeight: 900 }}>Roadmap</div>
                <div style={{ marginTop: 8 }} className="muted">Coming soon: multi-scene choreography, team permissions, more export codecs, advanced VFX packs, and mobile-first editing flows.</div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section style={{ padding: '12px 0 80px' }} className="oval-outline">
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
              <motion.h2 initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }} style={{ color: GOLD_HEX, fontSize: 20, fontWeight: 900 }} className="glow-gold">Frequently Asked</motion.h2>

              <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                {[
                  { q: 'What is The Story Club?', a: 'AI-first story studio that creates images, videos, music, narration and exports in multiple formats.' },
                  { q: 'Is it safe for kids?', a: 'Yes — content moderation and safety toggles are embedded. Parental controls are available.' },
                  { q: 'Can I export my animations & movies?', a: 'Export MP4, GIF, print-ready PDF storybooks, and share interactive cards.' },
                  { q: 'Does it support other languages?', a: 'Yes — multilingual generation and dubbing are supported for exports.' },
                ].map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="card" style={{ padding: 16, borderRadius: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>{f.q}</div>
                        <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.9)' }}>{f.a}</div>
                      </div>
                      <div style={{ color: GOLD_HEX, fontWeight: 900 }} aria-hidden>•</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <PortalOverlay />
      </div>
    </StoryContext.Provider>
  );
}
